import { useCallback, useEffect, useEffectEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
/**
 * Creates a `TabProvider` active tab store hook that persists the active tab
 * in a URL search param. A missing param, or one rejected by `isValidTabId`,
 * falls back to the provider's `initialTabId`; other search params and the
 * history entry's state are preserved on writes. The param is kept in the URL
 * while the hook is mounted and removed from it on unmount (unless the unmount
 * comes from a pathname change - the new page's params are not ours to edit).
 */
export const createUseUrlActiveTabStore = ({ isValidTabId, paramName, }) => {
    var _react_refresh_temp_1;
    _react_refresh_temp_1 = $RefreshSig$();
    const useUrlActiveTabStore = _react_refresh_temp_1((initialTabId) => {
        _react_refresh_temp_1();
        const [searchParams, setSearchParams] = useSearchParams();
        const location = useLocation();
        const { pathname } = location;
        // Passed to every write: without it, React Router replaces the history entry's state with null.
        const locationState = location.state;
        const paramValue = searchParams.get(paramName);
        const activeTabId = paramValue !== null && (isValidTabId?.(paramValue) ?? true)
            ? paramValue
            : initialTabId;
        const setActiveTabId = useCallback((newActiveId) => {
            setSearchParams((prev) => {
                prev.set(paramName, newActiveId);
                return prev;
            }, { state: locationState });
        }, [locationState, setSearchParams]);
        const writeParamOnMount = useEffectEvent(() => {
            const params = new URLSearchParams(getLiveRouterLocation().search);
            if (params.get(paramName) !== activeTabId) {
                params.set(paramName, activeTabId);
                setSearchParams(params, {
                    replace: true,
                    state: locationState,
                });
            }
        });
        const removeParamOnUnmount = useEffectEvent(() => {
            const liveLocation = getLiveRouterLocation();
            const liveParams = new URLSearchParams(liveLocation.search);
            const isSamePage = liveLocation.pathname === pathname;
            if (!isSamePage || !liveParams.has(paramName)) {
                return;
            }
            liveParams.delete(paramName);
            setSearchParams(liveParams, {
                replace: true,
                state: locationState,
            });
        });
        useEffect(() => {
            writeParamOnMount();
            return removeParamOnUnmount;
        }, []);
        return { activeTabId, setActiveTabId };
    }, "7uW5uGDsRro6WVi6RIl9arUoDKY=", false, () => [useSearchParams, useLocation, useEffectEvent, useEffectEvent]);
    return useUrlActiveTabStore;
};
/**
 * Router-agnostic current location: with a hash router (DesktopClient) the
 * route pathname and search live inside the hash, and `window.location`'s own
 * pathname/search do not reflect them. Neither router uses a `basename`, so
 * the parsed pathname is directly comparable with `useLocation().pathname`.
 */
const getLiveRouterLocation = () => {
    const { hash, pathname, search } = window.location;
    if (hash.startsWith('#/')) {
        const hashPath = hash.slice(1);
        const searchIndex = hashPath.indexOf('?');
        return searchIndex >= 0
            ? {
                pathname: hashPath.slice(0, searchIndex),
                search: hashPath.slice(searchIndex),
            }
            : { pathname: hashPath, search: '' };
    }
    return { pathname, search };
};
